import type { Config } from '@ml/config';
import { getLinkCandidates } from '../board/link.ts';
import { isCompleteMove } from '../board/moves.ts';
import type { Coord } from '../board/types.ts';
import type { Rng } from '../rng.ts';
import { linkContextOf } from '../resolve/classify.ts';
import { stepBattle } from './battle.ts';
import type { BattleAction, BattleState, StepResult } from './types.ts';

export type BotOptions = {
  /** Số lần đi ngẫu nhiên (mặc định 16). */
  readonly tries?: number;
  /** Độ dài chuỗi tối đa (mặc định 8). */
  readonly maxLength?: number;
};

/**
 * Bot gỡ lỗi/mô phỏng: đi ngẫu nhiên trên ứng viên nối (`getLinkCandidates`, có lọc tiền tố),
 * giữ nước hoàn chỉnh dài nhất. Không tìm được thì DFS lấy nước đầu tiên. RNG của bot tách
 * khỏi RNG trận nên log hành động tự đủ để replay. Trả `null` nếu bảng không có nước.
 */
export function botAction(
  state: BattleState,
  rng: Rng,
  config: Config,
  opts: BotOptions = {},
): BattleAction | null {
  const tries = opts.tries ?? 16;
  const maxLength = opts.maxLength ?? 8;
  const ctx = linkContextOf(state.hero, state.heroState, config);
  const board = state.board;
  let best: Coord[] | null = null;
  const starts = getLinkCandidates(board, [], config, ctx);
  if (starts.length === 0) return null;
  for (let t = 0; t < tries; t++) {
    const path: Coord[] = [starts[rng.nextInt(starts.length)]!];
    for (;;) {
      if (isCompleteMove(board, path, config, ctx) && (!best || path.length > best.length)) {
        best = path.slice();
      }
      if (path.length >= maxLength) break;
      const next = getLinkCandidates(board, path, config, ctx);
      if (next.length === 0) break;
      path.push(next[rng.nextInt(next.length)]!);
    }
  }
  best ??= firstMove(state, config, maxLength);
  return best ? { type: 'release', path: best } : null;
}

function firstMove(state: BattleState, config: Config, maxLength: number): Coord[] | null {
  const ctx = linkContextOf(state.hero, state.heroState, config);
  const dfs = (path: Coord[]): Coord[] | null => {
    if (path.length > 0 && isCompleteMove(state.board, path, config, ctx)) return path;
    if (path.length >= maxLength) return null;
    for (const c of getLinkCandidates(state.board, path, config, ctx)) {
      const found = dfs([...path, c]);
      if (found) return found;
    }
    return null;
  };
  return dfs([]);
}

/** Phát lại log hành động từ state đầu: cùng state + log → cùng kết quả (combat.md 6, PvP lockstep). */
export function replayBattle(
  initial: BattleState,
  actions: readonly BattleAction[],
  config: Config,
): StepResult {
  let state = initial;
  const events: StepResult['events'][number][] = [];
  for (const a of actions) {
    const r = stepBattle(state, a, config);
    state = r.state;
    events.push(...r.events);
  }
  return { state, events };
}
